import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Drawer } from '@mui/material'

import { useAppInfo } from '../../hooks/useVersion'
import { feedbackApi } from '../../services/feedbackApi'
import type { FeedbackRow } from '../../types/feedback'
import { useNotify } from '../shared/ToastProvider'
import {
  AppDrawerBody,
  AppDrawerHeader,
  AppDrawerShell,
  AppDrawerTabs,
  type AppDrawerTabDef,
  APP_DRAWER_BG,
  APP_DRAWER_SHADOW,
  APP_DRAWER_WIDTH,
} from '../shared/AppDrawer'
import { BOTTOM_CHROME_RESERVE_PX } from './bottomChrome'
import { useFeedbackUI } from './FeedbackUIContext'
import FeedbackForm from './FeedbackForm'
import MyFeedbackList from './MyFeedbackList'
import SupportTab from './SupportTab'
import { writeLastUsedTab, type TabKey } from './lastUsedTab'

interface HelpFeedbackDrawerProps {
  /** Optional FP context for the support tab + auto-attached context */
  franchiseData?: { fp_code: string; franchise_name: string; country: string } | null
}

const TAB_HEADER: Record<TabKey, { title: string; subtitle: string }> = {
  submit: {
    title: 'Give feedback',
    subtitle: "Tell us what's broken, slow, or confusing.",
  },
  mine: {
    title: 'My items',
    subtitle: "Feedback you've sent and its status.",
  },
  support: {
    title: 'Support',
    subtitle: 'How to reach the Evo team for urgent help.',
  },
}

const HelpFeedbackDrawer: React.FC<HelpFeedbackDrawerProps> = ({ franchiseData }) => {
  const { open, closeDrawer, initialTab, presetType } = useFeedbackUI()
  const { showSuccess } = useNotify()
  const { environment } = useAppInfo()
  const isDev = environment === 'development'
  const [tab, setTab] = useState<TabKey>(initialTab || 'submit')

  // "My items" is fetched here (not inside MyFeedbackList) so the tab badge can
  // show a count the moment the drawer opens, mirroring the eager Issues/Activity
  // counts in ExceptionDrawer rather than waiting for the tab to be visited.
  const [mineRows, setMineRows] = useState<FeedbackRow[]>([])
  const [mineLoading, setMineLoading] = useState(false)
  const [mineError, setMineError] = useState<string | null>(null)

  const loadMine = useCallback(async () => {
    setMineLoading(true)
    setMineError(null)
    try {
      setMineRows(await feedbackApi.listMine())
    } catch {
      setMineError('Could not load your feedback')
    } finally {
      setMineLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) {
      setTab(initialTab || 'submit')
      loadMine()
    }
  }, [open, initialTab, loadMine])

  useEffect(() => {
    if (open) writeLastUsedTab(tab)
  }, [open, tab])

  const handleSubmitted = () => {
    loadMine()
    showSuccess("Thanks - your feedback's been sent to the Evo team.")
    setTab('mine')
  }

  const handleMineUpdated = (updated: FeedbackRow) => {
    setMineRows(prev => prev.map(r => (r.id === updated.id ? updated : r)))
  }

  // Badge only once a count is known and non-zero - never a misleading "0".
  const tabs = useMemo<AppDrawerTabDef<TabKey>[]>(
    () => [
      { value: 'submit' as TabKey, label: 'Give feedback' },
      {
        value: 'mine' as TabKey,
        label: 'My items',
        count: mineRows.length > 0 ? mineRows.length : undefined,
      },
      { value: 'support' as TabKey, label: 'Support' },
    ],
    [mineRows.length],
  )

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            // Match ExceptionDrawer width formula so the two right-side
            // drawers render at identical widths across all viewports
            // (520px desktop, full-width below 520).
            width: `min(${APP_DRAWER_WIDTH}px, 100vw)`,
            height: isDev ? `calc(100% - ${BOTTOM_CHROME_RESERVE_PX}px)` : '100%',
            bgcolor: APP_DRAWER_BG,
            boxShadow: APP_DRAWER_SHADOW,
          },
        }}
      >
        <AppDrawerShell>
          <AppDrawerHeader
            eyebrow="HELP & FEEDBACK"
            title={TAB_HEADER[tab].title}
            subtitle={TAB_HEADER[tab].subtitle}
            onClose={closeDrawer}
          />

          <AppDrawerTabs tabs={tabs} value={tab} onChange={setTab} />

          {tab === 'submit' && (
            <FeedbackForm
              presetType={presetType}
              franchiseData={franchiseData}
              onSubmitted={handleSubmitted}
              onCancel={closeDrawer}
            />
          )}
          {tab === 'mine' && (
            <AppDrawerBody padding={{ p: 2 }}>
              <MyFeedbackList
                rows={mineRows}
                loading={mineLoading}
                error={mineError}
                onReload={loadMine}
                onUpdated={handleMineUpdated}
                onSwitchToSubmit={() => setTab('submit')}
              />
            </AppDrawerBody>
          )}
          {tab === 'support' && (
            <AppDrawerBody padding={{ p: 2 }}>
              <SupportTab country={franchiseData?.country} />
            </AppDrawerBody>
          )}
        </AppDrawerShell>
      </Drawer>
    </>
  )
}

export default HelpFeedbackDrawer
